import Component from '@glimmer/component'
import { service } from '@ember/service'
import { use } from 'ember-resources'
import { eq } from 'ember-truth-helpers'
import { t } from 'ember-intl'
import { keepLatest } from 'reactiveweb/keep-latest'
import { trackedFunction } from 'reactiveweb/function'
import type ReportingUiScopeRoute from 'teacher-ui-client/reporting/ui-scope/route'
import { toRequestScope } from 'teacher-ui-client/services/reporting'
import { RecentWritingCardLesson } from 'teacher-ui-client/components/writing-legends/recent-writing-card-lesson'
import type RecentWritingService from 'teacher-ui-client/services/recent-writing'
import { joinQueryParams } from 'teacher-ui-client/utils/uri'
import FidgetSpinnerWaveComponent from '@blakeelearning/fidget/components/fidget/spinner/wave'
import BasePageSelector from 'teacher-ui-client/components/base/page-selector'

export interface RecentWritingLessonItem {
  student_id: string
  blake_student_id: number
  first_name: string
  last_name: string
  avatar: string
  student_answers: string[]
  activity_id: string
  activity_title?: string
  lesson_result_id: number
  content_data_name: string
  attempt_count: number
  is_passed: boolean
  created_date: string
  map_number?: number
  lesson_number?: number
  classroom_name?: string
}

interface Signature {
  Args: {
    uiScope: ModelFor<ReportingUiScopeRoute>['uiScope']
    period: ModelFor<ReportingUiScopeRoute>['period']
    pageNumber: number
    perPage: number
  }
}

interface RecentWritingLessonData {
  data: RecentWritingLessonItem[]
  total: number
}

export class RecentWritingsListLesson extends Component<Signature> {
  @service recentWriting!: RecentWritingService

  fetchData = trackedFunction(this, () => {
    const requestScope = toRequestScope(this.args.uiScope)
    const queryPeriod = this.recentWriting.buildQueryPeriod(this.args.period)

    const url = this.recentWriting.buildUrl('lessonwriting')
    const joinedUrl = joinQueryParams(url, {
      fromDate: queryPeriod.start,
      toDate: queryPeriod.end,
      take: this.args.perPage,
      skip: (this.args.pageNumber - 1) * this.args.perPage,
      clId: requestScope.scope === 'school-class' ? requestScope.id : undefined,
      tId: requestScope.scope === 'teacher' ? requestScope.id : undefined,
    })

    return this.recentWriting.fetchData<RecentWritingLessonData>(joinedUrl.href)
  })

  @use
  latestData = keepLatest({
    value: () => this.fetchData.value,
    when: () => this.fetchData.isPending,
  })

  get recentLessonWritings() {
    return this.latestData?.data
  }

  get totalPages() {
    return Math.ceil((this.latestData?.total ?? 0) / this.args.perPage)
  }

  <template>
    {{#if this.fetchData.isPending}}
      <FidgetSpinnerWaveComponent @centered={{true}} />
    {{else}}
      {{#if (eq this.recentLessonWritings.length 0)}}
        <span class="text-center italic">{{t "pages.recentWriting.noDataAvailable"}}</span>
      {{else}}
        <ul class="space-y-3">
          {{#each this.recentLessonWritings as |writing|}}
            <RecentWritingCardLesson
              @studentId={{writing.student_id}}
              @firstName={{writing.first_name}}
              @lastName={{writing.last_name}}
              @avatar={{writing.avatar}}
              @activityId={{writing.activity_id}}
              @activityTitle={{writing.activity_title}}
              @studentAnswers={{writing.student_answers}}
              @createdDate={{writing.created_date}}
              @map={{writing.map_number}}
              @lesson={{writing.lesson_number}}
              @className={{writing.classroom_name}}
            />
          {{/each}}
        </ul>

        <BasePageSelector class="mt-8" @pageCount={{this.totalPages}} @itemCount={{this.latestData.total}} />
      {{/if}}
    {{/if}}
  </template>
}
